import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiService } from '../services/api.service';
import { useTenant } from './TenantContext';

// Create context
const FeatureFlagContext = createContext();

// Feature flag codes - high-level module codes
export const FEATURE_CODES = {
  PRODUCT: 'FLAG_MASTER_DATA_MODULE_001',
  INVENTORY: 'FLAG_INVENTORY_MODULE_001',
  FORECASTING: 'FLAG_FORECASTING_MODULE_001',
  ORDER: 'FLAG_ORDER_MANAGEMENT_MODULE_001',
  CONFIG: 'FLAG_CONFIG_MODULE_001',
  NETWORK: 'FLAG_INTEGRATION_MODULE_001', // Using integration as network for now
  OPTIMIZATION: 'FLAG_OPTIMIZATION_MODULE_001',
  SHIPMENT: 'FLAG_SHIPMENT_MODULE_001',
  DEVELOPER: 'DEVELOPER', // Custom code for developer features
  ADMIN: 'ADMIN', // Custom code for admin features
  
  // Install Base Module Feature Flags
  INSTALL_BASE: 'FLAG_INSTALL_BASE_MODULE_001',
  INSTALL_BASE_SPARE_PARTS: 'FLAG_INSTALLBASE_SPARE_PARTS_001',
  INSTALL_BASE_PREDICTIVE_SERVICE: 'PREDICTIVE_SERVICE_EVENT',
  
  // Additional feature flags for specific capabilities
  INVENTORY_ADVANCED_ALLOCATION: 'FLAG_INVENTORY_ADVANCED_ALLOCATION_001',
  INVENTORY_REAL_TIME_TRACKING: 'FLAG_INVENTORY_REAL_TIME_TRACKING_001',
  FORECASTING_ML_MODELS: 'FLAG_FORECASTING_ML_MODELS_001',
  FORECASTING_SEASONAL_ADJUSTMENT: 'FLAG_FORECASTING_SEASONAL_ADJUSTMENT_001',
  FORECASTING_CUSTOM_ALGORITHMS: 'FLAG_FORECASTING_ALGO_BRING_OWN_001',
  ORDER_AUTOMATED_ORDERING: 'FLAG_ORDER_MANAGEMENT_AUTOMATED_ORDERING_001',
  ORDER_SUPPLIER_INTEGRATION: 'FLAG_ORDER_MANAGEMENT_SUPPLIER_INTEGRATION_001',
  ORDER_CHANGE: 'FLAG_ORDER_MANAGEMENT_ORDER_CHANGE_001',
  CARRIER_FEDEX: 'FLAG_CARRIER_3PL_FEDEX_001',
  SHIPMENT_TELEMETRY: 'FLAG_SHIPMENT_TELEMETRY_001',
  USER_MANAGEMENT: 'FLAG_USER_MANAGEMENT_MODULE_001',
  REPORTING: 'FLAG_REPORTING_MODULE_001',
  MESSAGING: 'FLAG_MESSAGING_MODULE_001',
  
  // Add the new LLM Chat feature flag
  INSIGHTS_LLMCHAT: 'FLAG_INSIGHTS_LLMCHAT_001',
  
  // Invoice Processing Module
  INVOICE_PROCESSING: 'FLAG_INVOICE_PROCESSING_MODULE_001'
};

// Provider component
export function FeatureFlagProvider({ children }) {
  const [features, setFeatures] = useState(getDefaultFeatures());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { tenantAlias, isLoading: isTenantLoading } = useTenant();
  const [apiResponseReceived, setApiResponseReceived] = useState(false);

  // Get default features for fallback
  function getDefaultFeatures() {
    const isDev = process.env.NODE_ENV === 'development';
    
    // Create a feature object with all features enabled
    const defaultFeatures = {};
    
    // Enable all features by default
    Object.values(FEATURE_CODES).forEach(code => {
      defaultFeatures[code] = true;
    });
    
    // Always enable developer features in development mode
    defaultFeatures[FEATURE_CODES.DEVELOPER] = true;
    defaultFeatures[FEATURE_CODES.ADMIN] = true;
    
    return defaultFeatures;
  }

  // Fetch features when tenant is loaded
  useEffect(() => {
    async function fetchFeatures() {
      // Don't fetch if tenant is still loading
      if (isTenantLoading || !tenantAlias) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        console.log(`Fetching features for tenant: ${tenantAlias}`);
        
        // Call API to get tenant features using the new feature-codes endpoint
        const response = await apiService.get(`/features/${tenantAlias}/feature-codes`);
        
        if (response && response.feature_codes) {
          //console.log('API Response feature codes:', response.feature_codes);
          
          // Start with default features to ensure all keys exist
          const featureMap = getDefaultFeatures();
          
          // Merge the API response features
          Object.entries(response.feature_codes).forEach(([featureCode, isEnabled]) => {
            featureMap[featureCode] = isEnabled;
            console.log(`Set feature ${featureCode} to enabled: ${isEnabled}`);
          });
          
          // Always enable developer features in development mode
          featureMap[FEATURE_CODES.DEVELOPER] = true;
          
          if (process.env.NODE_ENV === 'development') {
            featureMap[FEATURE_CODES.ADMIN] = true;
          }
          
          //console.log('Final feature map:', featureMap);
          setFeatures(featureMap);
          setApiResponseReceived(true);
        } else {
          console.warn('Invalid feature response format:', response);
          setFeatures(getDefaultFeatures());
        }
      } catch (err) {
        console.error('Failed to load features:', err);
        setError(err.message || 'Failed to load features');
        // Set default features in case of error
        setFeatures(getDefaultFeatures());
      } finally {
        setIsLoading(false);
      }
    }

    fetchFeatures();
  }, [tenantAlias, isTenantLoading]);

  // Check if a feature is enabled
  const isFeatureEnabled = (featureCode) => {
    // Special case for developer features - always enabled
    if (featureCode === FEATURE_CODES.DEVELOPER) {
      return true;
    }
    
    // During loading, use default features
    if (isLoading) {
      const defaultFeatures = getDefaultFeatures();
      const isEnabled = defaultFeatures[featureCode] === true;
      //console.log(`[Loading] Feature ${featureCode} enabled: ${isEnabled}`);
      return isEnabled;
    }
    
    // If we've received an API response, respect the explicit enabled/disabled status
    if (apiResponseReceived) {
      const isEnabled = features[featureCode] === true;
      //console.log(`[API] Feature ${featureCode} enabled: ${isEnabled}`);
      return isEnabled;
    } else {
      // If no API response yet, default to enabled in development mode
      if (process.env.REACT_APP_ENV === 'Dev') {
        //console.log(`[Dev-Default] Feature ${featureCode} enabled: true (development mode)`);
        return true;
      }
    }
    
    // Default to false for unknown features in production
    //console.log(`[Production-Default] Feature ${featureCode} enabled: false (production default)`);
    return false;
  };

  // Get all features
  const getAllFeatures = () => {
    return { ...features };
  };

  // Context value
  const value = {
    isFeatureEnabled,
    isLoading,
    error,
    allFeatures: getAllFeatures(),
    apiResponseReceived
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

// Custom hook to use feature flags
export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}